<!-- Toolbar actions (Save / Cancel) are registered declaratively in the
     component via PageHeaderService.setPageActions(...). An effect()
     re-publishes them whenever the relevant form / saving state changes. -->

<div class="container mx-auto px-4 py-6 max-w-4xl">
  @if (isLoading) {
    <div class="flex justify-center items-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>
  } @else if (error && !product) {
    <div class="alert alert-error">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <span>{{ error }}</span>
    </div>
  } @else if (product) {

    @if (error) {
      <div class="alert alert-error mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <span>{{ error }}</span>
      </div>
    }

    <form [formGroup]="productForm">
      <!-- Product Info Card -->
      <div class="card bg-transparent mb-6">
        <div class="card-body">
          <h3 class="card-title text-lg mb-4">Product Information</h3>

          <!-- Barcodes/Codes (Editable) -->
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text font-semibold">Barcode(s)</span>
            </label>
            <app-tags-input
              formControlName="codes"
              variant="primary"
              placeholder="Type a barcode and press Enter">
            </app-tags-input>
          </div>

          <!-- Name Field -->
          <div class="form-control mb-4">
            <label class="label" for="product-name">
              <span class="label-text font-semibold">Name</span>
            </label>
            <ui-input
              type="text"
              [value]="productForm.get('name')?.value || ''"
              (valueChange)="productForm.get('name')?.setValue($event)"
              placeholder="Product name"
              [fullWidth]="true"
              ariaLabel="Product name">
            </ui-input>
          </div>

          <!-- Quantity Section -->
          <div class="bg-base-200 rounded-xl p-4 mb-4 space-y-3">
            <span class="text-sm font-semibold">Quantity</span>

            <div class="flex gap-3">
              <div class="flex-1">
                <label class="label py-1">
                  <span class="label-text text-xs text-base-content/60">Amount</span>
                </label>
                <ui-input
                  type="text"
                  [value]="productForm.get('quantityValue')?.value || ''"
                  (valueChange)="productForm.get('quantityValue')?.setValue($event)"
                  placeholder="0.0"
                  [fullWidth]="true"
                  ariaLabel="Quantity value">
                </ui-input>
              </div>
              <div class="w-36">
                <label class="label py-1">
                  <span class="label-text text-xs text-base-content/60">Unit</span>
                </label>
                <ui-select
                  [options]="quantityUnitOptions"
                  [value]="productForm.get('quantityUnit')?.value || ''"
                  (valueChange)="productForm.get('quantityUnit')?.setValue($event)"
                  [fullWidth]="true"
                  ariaLabel="Quantity unit">
                </ui-select>
              </div>
            </div>

            <div class="form-control">
              <label class="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  class="toggle toggle-primary toggle-sm"
                  [checked]="productForm.get('quantityType')?.value === 'variable'"
                  (change)="setQuantityType($any($event.target).checked ? 'variable' : 'fixed')"
                />
                <span class="label-text font-medium">Variable quantity</span>
              </label>
              @if (productForm.get('quantityType')?.value === 'variable') {
                <p class="text-xs text-base-content/60 ml-10">Each stock item has its own quantity (e.g. a 650g cut of cheese). The amount above is used as a default suggestion when adding stock.</p>
              } @else {
                <p class="text-xs text-base-content/60 ml-10">Fixed package size — every item has the same quantity.</p>
              }
            </div>
          </div>

          <!-- Brands Field -->
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text font-semibold">Brands</span>
            </label>
            <app-tags-input
              formControlName="brands"
              variant="info"
              placeholder="Type a brand and press Enter">
            </app-tags-input>
          </div>
        </div>
      </div>

      <!-- Default Expiry Card -->
      <div class="card bg-base-200">
        <div class="card-body">
          <h3 class="card-title text-lg mb-2">Default Expiry</h3>
          <p class="text-sm text-base-content/60 mb-4">
            Used for expiration estimation when you don't set an expiry date on stock items.
          </p>

          <div class="flex gap-3 items-center flex-wrap">
            <div class="w-32">
              <ui-input
                type="number"
                [value]="productForm.get('defaultExpiryValue')?.value || ''"
                (valueChange)="productForm.get('defaultExpiryValue')?.setValue(parseIntValue($event))"
                placeholder="0"
                ariaLabel="Default expiry value">
              </ui-input>
            </div>

            <ui-button-group>
              <ui-button
                [variant]="productForm.get('defaultExpiryUnit')?.value === 'days' ? 'primary' : 'ghost'"
                size="sm"
                (clicked)="setDefaultExpiryUnit('days')">
                days
              </ui-button>
              <ui-button
                [variant]="productForm.get('defaultExpiryUnit')?.value === 'weeks' ? 'primary' : 'ghost'"
                size="sm"
                (clicked)="setDefaultExpiryUnit('weeks')">
                weeks
              </ui-button>
              <ui-button
                [variant]="productForm.get('defaultExpiryUnit')?.value === 'months' ? 'primary' : 'ghost'"
                size="sm"
                (clicked)="setDefaultExpiryUnit('months')">
                months
              </ui-button>
              <ui-button
                [variant]="productForm.get('defaultExpiryUnit')?.value === 'years' ? 'primary' : 'ghost'"
                size="sm"
                (clicked)="setDefaultExpiryUnit('years')">
                years
              </ui-button>
            </ui-button-group>
          </div>
        </div>
      </div>
    </form>
  }
</div>

